<template>
    <div class="modal fade" id="availableBonuses" tabindex="-1" aria-labelledby="availableBonusesLabel"
        aria-hidden="true" data-bs-backdrop="false" style="background-color:rgba(0,0,0,.72);">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div v-if="!selectedBonus" class="modal-content bonuses-modal"
                :style="{ '--bonus-accent': skin.my_account.nav_active_color }">
                <div class="bonuses-hero">
                    <div class="bonuses-hero__copy">
                        <i class="fas fa-crown bonuses-hero__crown"></i>
                        <h5 id="availableBonusesLabel">BONOS</h5>
                        <p>MÁS JUEGO, MÁS EMOCIÓN PARA TI</p>
                    </div>
                    <div class="bonuses-hero__gift" aria-hidden="true"><i class="fas fa-gift"></i></div>
                    <span class="coin coin--one">$</span><span class="coin coin--two">$</span><span class="coin coin--three">$</span>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"><i class="fas fa-times"></i></button>
                </div>

                <div class="modal-body">
                    <div class="bonuses-tabs" role="tablist" aria-label="Bonos">
                        <button type="button" :class="{ 'is-active': activeTab === 'active' }" @click="loadActiveBonuses">
                            <i class="fas fa-bolt"></i> Bonos activos
                        </button>
                        <button type="button" :class="{ 'is-active': activeTab === 'available' }" @click="selectAvailableBonuses">
                            <i class="fas fa-gift"></i> Bonos disponibles
                        </button>
                    </div>

                    <template v-if="activeTab === 'active'">
                        <div class="bonuses-section-title"><h6>Tus bonos activos</h6><p>Aquí estarán los bonos que ya tienes activados.</p></div>
                        <div v-if="activeLoading" class="bonuses-state">
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Cargando bonos activos...
                        </div>
                        <div v-else-if="activeError" class="bonuses-state bonuses-state--error" role="alert">
                            <i class="fas fa-exclamation-circle"></i>{{ activeError }}
                        </div>
                        <div v-else-if="!activeBonuses.length" class="bonuses-state">
                            <i class="fas fa-info-circle"></i>Cuando actives nuevos bonos, aparecerán aquí.
                        </div>
                        <div v-else class="active-bonuses-list">
                            <article v-for="(bonus, index) in activeBonuses" :key="bonus.id || bonus._id || index"
                                class="active-bonus-card">
                                <div class="active-bonus-card__heading">
                                    <div>
                                        <h6>{{ activeBonusName(bonus, index) }}</h6>
                                        <span>{{ isPendingDeposit(bonus) ? 'Depósito requerido' : rolloverPercentage(bonus) + '% completado' }}</span>
                                    </div>
                                    <span class="active-bonus-card__status"
                                        :class="{ 'is-completed': rolloverData(bonus).completed, 'is-pending': isPendingDeposit(bonus) }">
                                        {{ isPendingDeposit(bonus) ? 'Pendiente de depósito' : (rolloverData(bonus).completed ? 'Completado' : 'Activo') }}
                                    </span>
                                </div>
                                <div v-if="isPendingDeposit(bonus)" class="active-bonus-card__deposit-notice">
                                    <i class="fas fa-wallet"></i>
                                    <span v-if="hasMinimumDepositAmount(bonus)">Debes realizar un depósito mínimo de <strong>{{ formatBonusValue(bonusField(bonus, 'minimum_deposit_amount')) }}</strong> para que este bono quede activo.</span>
                                    <span v-else>Debes realizar un depósito para que este bono quede activo.</span>
                                </div>
                                <div v-if="!isPendingDeposit(bonus)" class="rollover-progress" role="progressbar" :aria-valuenow="rolloverPercentage(bonus)"
                                    aria-valuemin="0" aria-valuemax="100">
                                    <span :style="{ width: rolloverPercentage(bonus) + '%' }"></span>
                                </div>
                                <div v-if="!isPendingDeposit(bonus)" class="rollover-values">
                                    <span>Progreso <strong>{{ formatBonusValue(rolloverData(bonus).progress) }}</strong></span>
                                    <span>Restante <strong>{{ formatBonusValue(rolloverData(bonus).remaining) }}</strong></span>
                                    <span>Requerido <strong>{{ formatBonusValue(rolloverData(bonus).required) }}</strong></span>
                                </div>
                                <button type="button" class="active-bonus-card__details"
                                    :style="[skin.header_desktop.selection, skin.header_desktop.nav_bar_text]"
                                    @click="openBonusDetails(bonus)">Ver detalles</button>
                            </article>
                        </div>
                    </template>
                    <div v-else-if="loading" class="bonuses-state">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Cargando bonos...
                    </div>
                    <div v-else-if="error" class="bonuses-state bonuses-state--error" role="alert">
                        <i class="fas fa-exclamation-circle"></i>{{ error }}
                    </div>
                    <div v-else-if="!bonuses.length" class="bonuses-state">
                        <i class="fas fa-info-circle"></i>No hay bonos disponibles en este momento.
                    </div>
                    <template v-else>
                        <div class="bonuses-section-title"><h6>Bonos disponibles</h6><p>Descubre todos los bonos que tenemos para ti.</p></div>
                        <div class="bonuses-list">
                            <article v-for="(bonus, index) in bonuses" :key="bonus.id || bonus._id || index" class="bonus-card">
                                <div class="bonus-card__icon"><i class="fas fa-gift" aria-hidden="true"></i></div>
                                <div class="bonus-card__content">
                                    <h6>{{ bonus.name || `Bono ${index + 1}` }}</h6>
                                    <p>{{ bonus.description || 'Sin descripción' }}</p>
                                    <div class="bonus-card__meta">
                                        <span class="bonus-card__value"><i class="fas fa-crown"></i> Hasta <strong>{{ formatBonusValueByType(bonus) }}</strong></span>
                                        <span class="bonus-card__dates"><i class="far fa-calendar-alt"></i> Del {{ formatDate(bonus.start_date) }} al {{ formatDate(bonus.end_date) }}</span>
                                    </div>
                                </div>
                                <div class="bonus-card__action">
                                    <button type="button" :style="[skin.header_desktop.selection, skin.header_desktop.nav_bar_text]"
                                        @click="openActivation(bonus, bonus.id)">
                                        Activar
                                    </button>
                                </div>
                            </article>
                        </div>
                    </template>
                </div>

            </div>
            <div v-else class="modal-content bonuses-modal activation-confirmation__card"
                :style="{ '--bonus-accent': skin.my_account.nav_active_color }"
                role="dialog" aria-modal="true" aria-labelledby="activationConfirmationTitle">
                <button type="button" class="activation-confirmation__close" :disabled="activatingBonus !== null"
                    aria-label="Cerrar" @click="closeActivation"><i class="fas fa-times"></i></button>
                <div class="activation-confirmation__gift"><i class="fas fa-gift"></i></div>
                <h6 id="activationConfirmationTitle">{{ bonusField(selectedBonus, 'name') || 'Detalle del bono' }}</h6>
                <strong class="activation-confirmation__value">Hasta {{ formatBonusValueByType(selectedBonus) }}</strong>
                <p>{{ bonusField(selectedBonus, 'description') || 'Revisa las condiciones de tu bono.' }}</p>

                <div class="activation-confirmation__details">
                    <div><span><i class="fas fa-crown"></i> Valor del bono</span><strong>{{ formatBonusValueByType(selectedBonus) }}</strong></div>
                    <div v-if="bonusField(selectedBonus, 'bonus_type') == 'DEPOSIT' && bonusField(selectedBonus, 'value_type') == 'PERCENTAGE'"><span><i class="far fa-calendar-check"></i> Bono máximo</span><strong>{{ formatBonusValue(bonusField(selectedBonus, 'maximum_bonus_amount')) }}</strong></div>
                    
                    <div><span><i class="fas fa-sync-alt"></i> Rollover</span><strong>X{{ formatBonusValue(bonusField(selectedBonus, 'rollover_multiplier')) }}</strong></div>
                    
                    <div v-if="selectedBonusMode === 'activate'"><span><i class="far fa-calendar-check"></i> Disponible desde</span><strong>{{ formatDate(bonusField(selectedBonus, 'start_date')) }}</strong></div>
                    <div v-if="selectedBonusMode === 'activate'"><span><i class="far fa-calendar-check"></i> Disponible hasta</span><strong>{{ formatDate(bonusField(selectedBonus, 'end_date')) }}</strong></div>
                    <div v-if="selectedBonusMode === 'details'"><span><i class="far fa-calendar-check"></i> Activado el</span><strong>{{ formatDate(bonusDates(selectedBonus).activated_at) }}</strong></div>
                    <div v-if="selectedBonusMode === 'details'"><span><i class="far fa-calendar-times"></i> Expira el</span><strong>{{ formatDate(bonusDates(selectedBonus).expires_at) }}</strong></div>
                </div>
                <div v-if="bonusField(selectedBonus, 'product')" class="activation-confirmation__product">
                    <i class="fas fa-dice"></i>
                    <span v-if="selectedBonusMode === 'details'">Para avanzar tu bono, debes apostar en <strong>{{ formatProduct(bonusField(selectedBonus, 'product')) }}</strong>.</span>
                    <span v-else>Este bono aplica para apuestas en <strong>{{ formatProduct(bonusField(selectedBonus, 'product')) }}</strong>.</span>
                </div>
                <div v-if="activationError" class="activation-confirmation__error" role="alert">{{ activationError }}</div>

                <button v-if="selectedBonusMode === 'activate'" type="button" class="activation-confirmation__submit"
                    :style="[skin.header_desktop.selection, skin.header_desktop.nav_bar_text]"
                    :disabled="activatingBonus !== null" @click="confirmActivation">
                    <span v-if="activatingBonus !== null" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span v-else>Confirmar activación</span>
                </button>
                <button v-if="selectedBonusMode === 'activate'" type="button" class="activation-confirmation__cancel" :disabled="activatingBonus !== null"
                    @click="closeActivation">Cancelar</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { getPromotionsApiKey, getPromotionsApiUrl } from '../../../services/promotionsApi';

export default {
    name: 'AvailableBonuses',
    props: {
        skin: { type: Object, required: true },
        user: { type: Object, required: true }
    },
    data() {
        return { activeTab: 'available', bonuses: [], loading: false, error: '', activeBonuses: [], activeLoading: false, activeError: '', activatingBonus: null, selectedBonus: null, selectedBonusId: null, selectedBonusMode: null, activationError: '' };
    },
    mounted() {
        this.$el.addEventListener('hide.bs.modal', this.releaseModalFocus);
    },
    beforeDestroy() {
        this.$el.removeEventListener('hide.bs.modal', this.releaseModalFocus);
    },
    methods: {
        releaseModalFocus() {
            if (this.$el.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        },
        promotionsHeaders() {
            return {
                'w-api-key': getPromotionsApiKey(this.user.domain),
                'w-api-token': `${this.$store.state.user_token}_${this.user.site_id}`
            };
        },
        selectAvailableBonuses() {
            this.activeTab = 'available';
            if (!this.loading) this.loadBonuses();
        },
        async loadActiveBonuses() {
            this.activeTab = 'active';
            const playerId = this.user?.user_data?.data?.user_id;
            if (!playerId) {
                this.activeError = 'No fue posible identificar al jugador.';
                return;
            }

            this.activeLoading = true;
            this.activeError = '';
            this.activeBonuses = [];
            try {
                const response = await axios.get(
                    `${getPromotionsApiUrl(this.user.domain)}/bonus/players/${encodeURIComponent(playerId)}/active-bonus-progress`,
                    { headers: this.promotionsHeaders() }
                );
                this.activeBonuses = this.normalizeActiveBonuses(response.data);
            } catch (error) {
                this.activeError = error.response?.data?.message || 'No fue posible consultar los bonos activos.';
            } finally {
                this.activeLoading = false;
            }
        },
        async loadBonuses() {
            this.activeTab = 'available';
            const playerId = this.user?.user_data?.data?.user_id;
            if (!playerId) {
                this.error = 'No fue posible identificar al jugador.';
                return;
            }

            this.loading = true;
            this.error = '';
            this.bonuses = [];
            try {
                const response = await axios.get(
                    `${getPromotionsApiUrl(this.user.domain)}/bonus/players/${encodeURIComponent(playerId)}/available-bonuses`,
                    { headers: this.promotionsHeaders() }
                );
                this.bonuses = this.normalizeBonuses(response.data);
            } catch (error) {
                this.error = error.response?.data?.message || 'No fue posible consultar los bonos disponibles.';
            } finally {
                this.loading = false;
            }
        },
        openActivation(bonus, bonusId) {
            this.selectedBonus = bonus;
            console.log('Selected bonus:', bonus);
            this.selectedBonusId = bonusId;
            this.selectedBonusMode = 'activate';
            this.activationError = '';
        },
        openBonusDetails(bonus) {
            this.selectedBonus = bonus;
            this.selectedBonusId = null;
            this.selectedBonusMode = 'details';
            this.activationError = '';
        },
        closeActivation() {
            if (this.activatingBonus !== null) return;
            this.selectedBonus = null;
            this.selectedBonusId = null;
            this.selectedBonusMode = null;
            this.activationError = '';
        },
        async confirmActivation() {
            const playerId = this.user?.user_data?.data?.user_id;
            if (!playerId || !this.selectedBonus || this.activatingBonus !== null) return;

            const bonusId = this.selectedBonusId;
            this.activatingBonus = bonusId;
            this.activationError = '';
            try {
                await axios.post(`${getPromotionsApiUrl(this.user.domain)}/bonus/player-bonuses/grant`, {
                    player_id: playerId,
                    bonus_definition_id: bonusId
                }, {
                    headers: this.promotionsHeaders()
                });
                this.selectedBonus = null;
                this.selectedBonusId = null;
                this.selectedBonusMode = null;
                await this.loadActiveBonuses();
            } catch (error) {
                this.activationError = error.response?.data?.message || 'No fue posible activar el bono.';
            } finally {
                this.activatingBonus = null;
            }
        },
        normalizeBonuses(payload) {
            if (Array.isArray(payload)) return payload;
            if (Array.isArray(payload?.data)) return payload.data;
            if (Array.isArray(payload?.bonuses)) return payload.bonuses;
            if (Array.isArray(payload?.bonus)) return payload.bonus;
            return payload && typeof payload === 'object' ? [payload] : [];
        },
        normalizeActiveBonuses(payload) {
            const data = payload?.data ?? payload;
            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.active_bonuses)) return data.active_bonuses;
            if (Array.isArray(data?.bonuses)) return data.bonuses;
            return data && typeof data === 'object' ? [data] : [];
        },
        rolloverData(bonus) {
            return bonus?.rollover || bonus?.progress?.rollover || {};
        },
        rolloverPercentage(bonus) {
            const percentage = Number(this.rolloverData(bonus).percentage) || 0;
            return Math.min(100, Math.max(0, percentage));
        },
        activeBonusName(bonus, index) {
            return bonus.name || bonus.bonus_name || bonus.bonus?.name || bonus.bonus_definition?.name || `Bono activo ${index + 1}`;
        },
        activeBonusStatus(bonus) {
            return bonus.status || bonus.bonus?.status || bonus.bonus_definition?.status || '';
        },
        isPendingDeposit(bonus) {
            return this.activeBonusStatus(bonus) === 'PENDING_DEPOSIT';
        },
        hasMinimumDepositAmount(bonus) {
            const minimumDepositAmount = this.bonusField(bonus, 'minimum_deposit_amount');
            return minimumDepositAmount !== null && minimumDepositAmount !== undefined;
        },
        bonusField(bonus, field) {
            return bonus?.[field] ?? bonus?.bonus?.[field] ?? bonus?.bonus_definition?.[field];
        },
        bonusDates(bonus) {
            return bonus?.dates || bonus?.bonus?.dates || bonus?.bonus_definition?.dates || {};
        },
        formatProduct(product) {
            const labels = {
                CASINO: 'Casino',
                SPORTS: 'Deportes',
                SPORT: 'Deportes',
                LIVE_CASINO: 'Casino en vivo'
            };
            return labels[product] || String(product).replace(/_/g, ' ').toLowerCase();
        },
        formatDate(value) {
            if (!value) return 'fecha no definida';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return new Intl.DateTimeFormat('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
                .format(date).replace(/\./g, '');
        },
        formatBonusValue(value) {
            if (value === null || value === undefined || value === '') return 'No definido';
            const number = Number(value);
            return Number.isNaN(number) ? value : new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(number);
        },
        formatBonusValueByType(bonus) {
            const value = this.bonusField(bonus, 'bonus_value');
            const formattedValue = this.formatBonusValue(value);
            return this.bonusField(bonus, 'value_type') === 'PERCENTAGE' && value !== null && value !== undefined && value !== ''
                ? `${formattedValue}%`
                : formattedValue;
        }
    }
};
</script>

<style scoped>
.bonuses-modal {
    background: radial-gradient(circle at 85% 0, rgba(225,235,0,.08), transparent 32%), #080f14;
    border: 1px solid #1e2932; border-radius: 14px; color: #f2f4f5; font-family: "Roboto", Arial, sans-serif; overflow: hidden;
}
.bonuses-hero {
    align-items: center; background: radial-gradient(circle at 75% 50%, rgba(238,229,0,.3), transparent 28%), linear-gradient(110deg,#070b14 15%,#171b0a 60%,#080b10);
    border-bottom: 1px solid #28321b; display: flex; height: 166px; overflow: hidden; padding: 24px 42px; position: relative;
}
.bonuses-hero__copy { position: relative; z-index: 2; }
.bonuses-hero__copy h5 { color: #fff; font-size: 3.15rem; font-style: italic; font-weight: 900; letter-spacing: -.04em; line-height: .9; margin: 0; text-shadow: 0 4px 12px #000; }
.bonuses-hero__copy p { color: var(--bonus-accent); font-size: .78rem; font-style: italic; font-weight: 800; margin: 8px 0 0; }
.bonuses-hero__crown { color: var(--bonus-accent); display: block; font-size: 1.1rem; margin: 0 0 5px 38px; }
.bonuses-hero__gift { color: #f3d21f; filter: drop-shadow(0 0 18px rgba(236,243,25,.35)); font-size: 7.5rem; position: absolute; right: 75px; top: 22px; transform: rotate(-7deg); }
.coin { align-items: center; background: linear-gradient(135deg,#fff36b,#d99400); border: 2px solid #fff17c; border-radius: 50%; color: #805500; display: flex; font-size: .7rem; font-weight: 900; height: 24px; justify-content: center; position: absolute; width: 24px; }
.coin--one { right: 210px; top: 26px; }.coin--two { right: 54px; top: 18px; }.coin--three { bottom: 12px; right: 65px; }
.bonuses-hero .btn-close { background: rgba(0,0,0,.38); border-radius: 50%; color: #fff; font-size: 1rem; height: 34px; opacity: 1; padding: 0; position: absolute; right: 14px; top: 14px; width: 34px; z-index: 3; }
.bonuses-modal .modal-body { padding: 14px 18px 22px; }
.bonuses-tabs { background: #081016; border: 1px solid #1d2830; border-radius: 10px; display: flex; margin-bottom: 20px; padding: 3px; width: 100%; }
.bonuses-tabs button { background: transparent; border: 1px solid transparent; border-radius: 8px; color: #8d99a3; flex: 1; font-weight: 600; min-width: 205px; padding: 12px 18px; transition: .2s ease; }
.bonuses-tabs button i { font-size: 1rem; margin-right: 9px; }
.bonuses-tabs button.is-active { border-color: var(--bonus-accent); box-shadow: 0 0 12px var(--bonus-accent), inset 0 0 12px var(--bonus-accent); color: #fff; }
.bonuses-tabs button.is-active i { color: var(--bonus-accent); }
.bonuses-section-title { margin: 0 4px 16px; }
.bonuses-section-title h6 { color: #fff; font-size: 1.2rem; font-weight: 700; margin-bottom: 5px; }
.bonuses-section-title p { color: #8f9aa3; font-size: .8rem; margin: 0; }
.bonuses-state { align-items: center; background: linear-gradient(135deg,#121c24,#101820); border: 1px solid #293945; border-radius: 10px; color: #aeb8bf; display: flex; gap: 14px; justify-content: center; min-height: 64px; padding: 16px 20px; }
.bonuses-state > i { color: #93a2ad; font-size: 1.2rem; }.bonuses-state--error { border-color: #7c3434; color: #ffbaba; }
.active-bonuses-list { display: flex; flex-direction: column; gap: 12px; }
.active-bonus-card { background: rgba(255,255,255,.025); border: 1px solid rgba(255,255,255,.12); border-radius: 10px; padding: 16px 18px; }
.active-bonus-card__heading { align-items: center; display: flex; justify-content: space-between; margin-bottom: 13px; }
.active-bonus-card__heading h6 { color: #fff; font-size: 1rem; font-weight: 700; margin: 0 0 3px; }
.active-bonus-card__heading > div > span { color: #9ba4aa; font-size: .75rem; }
.active-bonus-card__status { border: 1px solid var(--bonus-accent); border-radius: 20px; color: var(--bonus-accent); font-size: .7rem; font-weight: 700; padding: 5px 11px; }
.active-bonus-card__status.is-completed { background: var(--bonus-accent); color: #fff; }
.active-bonus-card__status.is-pending { max-width: 145px; text-align: center; }
.active-bonus-card__deposit-notice { align-items: center; background: rgba(255,255,255,.05); border-left: 3px solid var(--bonus-accent); border-radius: 6px; display: flex; font-size: .78rem; gap: 10px; margin: 0 0 14px; padding: 10px 12px; }
.active-bonus-card__deposit-notice i,.active-bonus-card__deposit-notice strong { color: var(--bonus-accent); }
.rollover-progress { background: rgba(255,255,255,.12); border-radius: 20px; height: 9px; overflow: hidden; width: 100%; }
.rollover-progress > span { background: var(--bonus-accent); border-radius: inherit; display: block; height: 100%; min-width: 0; transition: width .35s ease; }
.rollover-values { display: flex; gap: 18px; justify-content: space-between; margin-top: 11px; }
.rollover-values span { color: #8f9aa3; display: flex; flex-direction: column; font-size: .68rem; }
.rollover-values span:nth-child(2) { text-align: center; }.rollover-values span:last-child { text-align: right; }
.rollover-values strong { color: #fff; font-size: .8rem; margin-top: 2px; }
.active-bonus-card__details { border: 0; border-radius: 8px; display: block; font-size: .75rem; font-weight: 700; margin: 14px 0 0 auto; min-width: 112px; padding: 8px 14px; }
.bonuses-list { display: flex; flex-direction: column; gap: 12px; }
.bonus-card { align-items: center; border-radius: 10px; box-shadow: inset 0 0 25px #323030; display: flex; min-height: 108px; overflow: hidden; padding: 14px 18px; }
.bonus-card__icon { align-items: center;     background: radial-gradient(circle, #f6df38 0, #b87500 51%, #422000 64%, transparent 51%); color: #fff200; display: flex; flex: 0 0 86px; font-size: 2.8rem; height: 78px; justify-content: center; margin-right: 18px; text-shadow: 0 3px 6px #704500; }
.bonus-card__content { min-width: 0; width: 100%; }.bonus-card h6 { color: #fff; font-size: 1rem; font-weight: 700; margin: 0 0 4px; }
.bonus-card__content > p { color: #9ba4aa; font-size: .78rem; margin: 0 0 11px; }.bonus-card__meta { align-items: center; display: flex; flex-wrap: wrap; gap: 10px 24px; }
.bonus-card__value,.bonus-card__dates { align-items: center; color: #adb5bb; display: flex; font-size: .76rem; gap: 6px; }.bonus-card__value i,.bonus-card__value strong { color: var(--bonus-accent); }
.bonus-card__action { align-items: center; display: flex; flex: 0 0 115px; flex-direction: column; gap: 6px; margin-left: 16px; }
.bonus-card__action button {background: var(--bonus-accent);color:white; border: 0; border-radius: 9px; font-weight: 700; min-height: 34px; padding: 9px 22px; width: 100%; }
.bonus-card__action button:disabled { cursor: wait; opacity: .65; }
.bonus-card__action small { color: #82d99a; font-size: .68rem; line-height: 1.2; text-align: center; }
.bonus-card__action small.is-error { color: #ff9b9b; }
.activation-confirmation__card { align-self: center; box-shadow: 0 24px 65px rgba(0,0,0,.65); margin: 0 auto; max-width: 410px; min-height: 0; padding: 30px 26px 18px; position: relative; text-align: center; width: calc(100% - 32px); }
.activation-confirmation__close { align-items: center; background: transparent; border: 0; border-radius: 50%; color: currentColor; display: flex; font-size: 1rem; height: 32px; justify-content: center; position: absolute; right: 10px; top: 9px; width: 32px; }
.activation-confirmation__gift { align-items: center; border: 1px solid var(--bonus-accent); border-radius: 50%; color: var(--bonus-accent); display: flex; filter: drop-shadow(0 0 10px var(--bonus-accent)); font-size: 2.7rem; height: 72px; justify-content: center; line-height: 1; margin: 0 auto 14px; width: 72px; }
.activation-confirmation__card h6 { color: currentColor; font-size: 1.2rem; font-weight: 700; margin: 0 0 4px; }
.activation-confirmation__value { color: var(--bonus-accent); display: block; font-size: 1.55rem; line-height: 1.2; }
.activation-confirmation__card > p { color: currentColor; font-size: .78rem; line-height: 1.45; margin: 10px 0 14px; opacity: .75; }
.activation-confirmation__details { border-bottom: 1px solid currentColor; border-top: 1px solid currentColor; margin-bottom: 16px; padding: 7px 0; }
.activation-confirmation__details div { align-items: center; display: flex; font-size: .78rem; justify-content: space-between; padding: 6px 2px; }
.activation-confirmation__details span { align-items: center; display: flex; gap: 8px; opacity: .8; }
.activation-confirmation__details i { color: var(--bonus-accent); width: 15px; }
.activation-confirmation__product { align-items: center; background: rgba(255,255,255,.05); border-left: 3px solid var(--bonus-accent); border-radius: 6px; display: flex; font-size: .78rem; gap: 10px; margin: -5px 0 16px; padding: 10px 12px; text-align: left; }
.activation-confirmation__product i,.activation-confirmation__product strong { color: var(--bonus-accent); }
.activation-confirmation__error { color: #ff9b9b; font-size: .75rem; margin: -6px 0 12px; }
.activation-confirmation__submit { border: 0; border-radius: 8px; font-size: .82rem; font-weight: 700; min-height: 42px; width: 100%; }
.activation-confirmation__cancel { background: transparent; border: 0; color: currentColor; font-size: .76rem; margin-top: 10px; opacity: .75; padding: 5px 15px; }
.activation-confirmation button:disabled { cursor: wait; opacity: .6; }
@media only screen and (max-width:700px) {
    .bonuses-hero { height: 135px; padding: 20px 24px; }.bonuses-hero__copy h5 { font-size: 2.35rem; }.bonuses-hero__gift { font-size: 5.5rem; opacity: .72; right: 40px; top: 35px; }
    .bonuses-modal .modal-body { padding: 12px; }.bonuses-tabs { width: 100%; }.bonuses-tabs button { flex: 1; font-size: .76rem; min-width: 0; padding: 10px 5px; }
    .bonus-card { align-items: start; display: grid; gap: 0 12px; grid-template-columns: 58px minmax(0, 1fr); padding: 14px; }
    .bonus-card__icon { flex-basis: auto; font-size: 2rem; height: 58px; margin: 0; width: 58px; }
    .bonus-card__content { width: auto; }
    .bonus-card__meta { align-items: flex-start; flex-direction: column; gap: 5px; }
    .bonus-card__action { grid-column: 1 / -1; margin: 14px 0 0; width: 100%; }
    .bonus-card__action button { font-size: .88rem; min-height: 44px; padding: 10px 16px; width: 100%; }
    .bonus-card__action small { margin-top: 2px; }
    .activation-confirmation__card { max-width: 360px; padding: 26px 18px 15px; width: calc(100% - 24px); }
    .activation-confirmation__gift { font-size: 2.25rem; height: 62px; width: 62px; }
    .rollover-values { gap: 8px; }.rollover-values strong { font-size: .72rem; }
}
</style>
